/**
 * FIFO Costing Service
 * 
 * Implements First In, First Out (FIFO) inventory valuation method.
 * Tracks cost layers for each purchase and consumes from oldest layers first.
 * 
 * @module services/fifo-service
 */

import { CostLayer, FIFOTransaction, FIFOCalculationResult, CostLayerSummary } from '@/types/cost-layer';

/**
 * In-memory storage for cost layers (demo mode)
 * In production, this would be stored in a database
 */
let costLayersStore: CostLayer[] = [];

/**
 * Generate a unique ID for cost layers
 */
const generateId = (): string => {
  return `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Add a new cost layer when receiving stock
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @param locationCode - Location code (optional)
 * @param quantity - Quantity received
 * @param unitCost - Cost per unit
 * @param reference - Reference number (PO, GRN, etc.)
 * @param purchaseDate - Purchase date (optional, defaults to now)
 * @returns The created cost layer
 * 
 * @example
 * // Receive 100 units at £10 each
 * const layer = addCostLayer('ITEM001', 'WH01', 'A1', 100, 10, 'PO-001');
 */
export const addCostLayer = (
  stockCode: string,
  warehouseCode: string,
  locationCode: string | undefined,
  quantity: number,
  unitCost: number,
  reference?: string,
  purchaseDate?: Date | string
): CostLayer => {
  const layer: CostLayer = {
    id: generateId(),
    stockCode,
    warehouseCode,
    locationCode,
    purchaseDate: purchaseDate ? new Date(purchaseDate) : new Date(),
    quantity,
    unitCost,
    remainingQuantity: quantity,
    reference,
    createdAt: new Date(),
  };

  costLayersStore.push(layer);
  return layer;
};

/**
 * Get all cost layers for a stock item
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code (optional, filters by warehouse if provided)
 * @returns Array of cost layers, sorted by purchase date (oldest first)
 */
export const getCostLayers = (
  stockCode: string,
  warehouseCode?: string
): CostLayer[] => {
  let layers = costLayersStore.filter(
    (layer) => layer.stockCode === stockCode && layer.remainingQuantity > 0
  );

  if (warehouseCode) {
    layers = layers.filter((layer) => layer.warehouseCode === warehouseCode);
  }

  // Sort by purchase date (oldest first - FIFO)
  return layers.sort((a, b) => a.purchaseDate.getTime() - b.purchaseDate.getTime());
};

/**
 * Calculate COGS using FIFO method
 * 
 * Consumes from the oldest cost layers first.
 * Returns detailed breakdown of which layers were used.
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @param quantity - Quantity being sold/consumed
 * @returns FIFO calculation result with cost breakdown
 * 
 * @example
 * // Sell 150 units using FIFO
 * const result = calculateFIFOCost('ITEM001', 'WH01', 150);
 * // Result shows which layers were consumed and total cost
 */
export const calculateFIFOCost = (
  stockCode: string,
  warehouseCode: string,
  quantity: number
): FIFOCalculationResult => {
  const layers = getCostLayers(stockCode, warehouseCode);
  const layersUsed: FIFOTransaction[] = [];
  let remainingToConsume = quantity;
  let totalCost = 0;

  // Consume from oldest layers first
  for (const layer of layers) {
    if (remainingToConsume <= 0) break;

    const quantityFromThisLayer = Math.min(remainingToConsume, layer.remainingQuantity);
    const costFromThisLayer = quantityFromThisLayer * layer.unitCost;

    layersUsed.push({
      layerId: layer.id,
      quantityUsed: quantityFromThisLayer,
      unitCost: layer.unitCost,
      totalCost: costFromThisLayer,
    });

    totalCost += costFromThisLayer;
    remainingToConsume -= quantityFromThisLayer;

    // Update remaining quantity in the layer
    layer.remainingQuantity -= quantityFromThisLayer;
  }

  // Calculate average cost for this transaction
  const averageCost = quantity > 0 ? totalCost / quantity : 0;

  // Get remaining layers after consumption
  const remainingLayers = layers.filter((layer) => layer.remainingQuantity > 0);

  return {
    totalCost: parseFloat(totalCost.toFixed(2)),
    averageCost: parseFloat(averageCost.toFixed(4)),
    layersUsed,
    remainingLayers,
  };
};

/**
 * Get the current FIFO cost (cost of next unit to be sold)
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Unit cost of the oldest layer, or 0 if no layers exist
 */
export const getCurrentFIFOCost = (
  stockCode: string,
  warehouseCode: string
): number => {
  const layers = getCostLayers(stockCode, warehouseCode);
  return layers.length > 0 ? layers[0].unitCost : 0;
};

/**
 * Get weighted average cost across all layers
 * 
 * This is useful for reporting and comparison purposes.
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Weighted average cost across all layers
 */
export const getWeightedAverageCost = (
  stockCode: string,
  warehouseCode: string
): number => {
  const layers = getCostLayers(stockCode, warehouseCode);
  
  if (layers.length === 0) return 0;

  const totalValue = layers.reduce(
    (sum, layer) => sum + layer.remainingQuantity * layer.unitCost,
    0
  );
  const totalQuantity = layers.reduce((sum, layer) => sum + layer.remainingQuantity, 0);

  return totalQuantity > 0 ? parseFloat((totalValue / totalQuantity).toFixed(4)) : 0;
};

/**
 * Get cost layer summary for a stock item
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Summary of cost layers
 */
export const getCostLayerSummary = (
  stockCode: string,
  warehouseCode: string
): CostLayerSummary => {
  const layers = getCostLayers(stockCode, warehouseCode);

  const totalQuantity = layers.reduce((sum, layer) => sum + layer.remainingQuantity, 0);
  const totalValue = layers.reduce(
    (sum, layer) => sum + layer.remainingQuantity * layer.unitCost,
    0
  );

  const dates = layers.map((layer) => layer.purchaseDate);
  const oldestLayerDate = dates.length > 0 ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null;
  const newestLayerDate = dates.length > 0 ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;

  return {
    stockCode,
    warehouseCode,
    totalQuantity,
    totalValue: parseFloat(totalValue.toFixed(2)),
    weightedAverageCost: totalQuantity > 0 ? parseFloat((totalValue / totalQuantity).toFixed(4)) : 0,
    oldestLayerDate,
    newestLayerDate,
    layerCount: layers.length,
  };
};

/**
 * Adjust cost layers for stock adjustments (write-offs, corrections)
 * 
 * For negative adjustments, consume from oldest layers first (FIFO).
 * For positive adjustments without a cost, use the weighted average cost.
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @param quantityAdjustment - Quantity adjustment (positive or negative)
 * @param unitCost - Unit cost (optional, for positive adjustments)
 * @param reference - Reference for the adjustment
 * @returns Adjustment result
 */
export const adjustCostLayers = (
  stockCode: string,
  warehouseCode: string,
  quantityAdjustment: number,
  unitCost?: number,
  reference?: string
): { success: boolean; message: string; cost?: number } => {
  if (quantityAdjustment === 0) {
    return { success: true, message: 'No adjustment needed' };
  }

  if (quantityAdjustment > 0) {
    // Positive adjustment - add a new layer
    const costToUse = unitCost || getWeightedAverageCost(stockCode, warehouseCode);
    addCostLayer(stockCode, warehouseCode, undefined, quantityAdjustment, costToUse, reference);
    return {
      success: true,
      message: `Added ${quantityAdjustment} units at ${costToUse.toFixed(2)} per unit`,
      cost: costToUse,
    };
  } else {
    // Negative adjustment - consume from oldest layers
    const result = calculateFIFOCost(stockCode, warehouseCode, Math.abs(quantityAdjustment));
    return {
      success: true,
      message: `Removed ${Math.abs(quantityAdjustment)} units using FIFO`,
      cost: result.averageCost,
    };
  }
};

/**
 * Clear all cost layers for a stock item (for testing/demo purposes)
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code (optional)
 */
export const clearCostLayers = (stockCode: string, warehouseCode?: string): void => {
  if (warehouseCode) {
    costLayersStore = costLayersStore.filter(
      (layer) => !(layer.stockCode === stockCode && layer.warehouseCode === warehouseCode)
    );
  } else {
    costLayersStore = costLayersStore.filter((layer) => layer.stockCode !== stockCode);
  }
};

/**
 * Get all cost layers (for debugging/admin purposes)
 */
export const getAllCostLayers = (): CostLayer[] => {
  return [...costLayersStore];
};

/**
 * Initialize demo cost layers for testing
 */
export const initializeDemoCostLayers = (): void => {
  // Clear existing layers
  costLayersStore = [];

  // Add some demo layers for testing
  const demoItems = [
    { code: 'LAPTOP-001', warehouse: 'WH01', location: 'A1' },
    { code: 'MOUSE-001', warehouse: 'WH01', location: 'B2' },
    { code: 'KEYBOARD-001', warehouse: 'WH01', location: 'B3' },
  ];

  demoItems.forEach((item) => {
    // Add multiple layers with different costs to demonstrate FIFO
    addCostLayer(item.code, item.warehouse, item.location, 50, 100, 'PO-001');
    
    // Simulate a second purchase at a higher cost
    setTimeout(() => {
      addCostLayer(item.code, item.warehouse, item.location, 30, 110, 'PO-002');
    }, 100);
    
    // Simulate a third purchase at an even higher cost
    setTimeout(() => {
      addCostLayer(item.code, item.warehouse, item.location, 20, 120, 'PO-003');
    }, 200);
  });
};

/**
 * Export cost layers to JSON (for backup/export)
 */
export const exportCostLayers = (): string => {
  return JSON.stringify(costLayersStore, null, 2);
};

/**
 * Import cost layers from JSON (for restore/import)
 */
export const importCostLayers = (json: string): { success: boolean; message: string } => {
  try {
    const imported = JSON.parse(json);
    if (!Array.isArray(imported)) {
      return { success: false, message: 'Invalid format: expected an array' };
    }
    
    // Convert date strings back to Date objects
    costLayersStore = imported.map((layer) => ({
      ...layer,
      purchaseDate: new Date(layer.purchaseDate),
      createdAt: new Date(layer.createdAt),
    }));
    
    return { success: true, message: `Imported ${costLayersStore.length} cost layers` };
  } catch (error) {
    return { success: false, message: `Import failed: ${error}` };
  }
};
